import clsx from "clsx";
import { useMemo, useState } from "react";

import { Domain } from "~/api/Api.ts";
import { Icon } from "~/assets/icons/Icon.tsx";
import NoData from "~/components/containers/NoData.tsx";
import { isDomainPrerenderingStatusHealthy } from "~/components/siteSpeed/helpers.tsx";
import { useSites } from "~/components/siteSpeed/hooks";
import { SiteModal } from "~/components/siteSpeed/sitesManager/SiteModal";
import Spinner from "~/components/ui/spinner/Spinner.tsx";
import { routes } from "~/router";

import styles from "./AiVisibilityEngineStatus.module.scss";

type Props = {
  domain: Domain;
  className?: string;
};

export const AiVisibilityEngineStatus = ({ domain, className }: Props) => {
  const [open, setOpen] = useState(false);
  const [siteId, setSiteId] = useState<null | number>(null);

  const isStatusHealthy = isDomainPrerenderingStatusHealthy(domain);
  const toggle = () => setOpen((v) => !v);

  const { data: sitesData, isPending: isPendingSitesData } = useSites(
    domain.id,
  );
  const sites = sitesData?.sites;
  const canCreate = !!sitesData?.authorizations?.collection?.includes("create");

  // order apex first and then alphabetically
  const orderedSites = useMemo(() => {
    const domainName = domain.name?.toLowerCase();

    return [...(sites ?? [])].sort((a, b) => {
      const aApex = a.name?.toLowerCase() === domainName;
      const bApex = b.name?.toLowerCase() === domainName;

      if (aApex !== bApex) return aApex ? -1 : 1;

      return (a.name ?? "").localeCompare(b.name ?? "");
    });
  }, [domain.name, sites]);

  return (
    <>
      <div
        className={clsx(styles.card, className)}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") toggle();
        }}
      >
        <div className={styles.wrapper}>
          <div>
            <h2 className={styles.title}>
              AI Visibility Engine Status
              {isStatusHealthy ? (
                <Icon
                  iconName="GreenCheckmarkIcon"
                  width="1rem"
                  height="1rem"
                />
              ) : (
                <Icon iconName="WarningIcon" width="1rem" height="1rem" />
              )}
            </h2>
            <p className={styles.description}>
              Monitor the AI Visibility engine status across all sites for this
              domain
            </p>
          </div>
          <span
            className={`${styles.arrow} ${open ? styles.arrowOpen : ""}`}
            aria-hidden="true"
          >
            <Icon
              iconName="IconDown"
              color="var(--functional-icons)"
              width="1rem"
              height="1rem"
            />
          </span>
        </div>
        {open && (
          <div
            className={styles.sitesWrapper}
            onClick={(e) => e.stopPropagation()}
          >
            {isPendingSitesData ? (
              <Spinner />
            ) : orderedSites.length > 0 ? (
              <div className={styles.sitesList}>
                {orderedSites.map((site) => (
                  <div
                    key={site.id}
                    className={styles.siteWrapper}
                    onClick={() => setSiteId(site.id)}
                  >
                    <div className={styles.siteDetails}>
                      <div>
                        <span className={styles.siteIcon}>🌐</span>
                      </div>
                      <div className={styles.siteName}>{site.name}</div>
                    </div>
                    <div
                      className={clsx(styles.siteStatus, {
                        [styles.statusEnabled]:
                          site.prerendering_status === "enabled",
                        [styles.statusRejected]:
                          site.prerendering_status === "rejected",
                        [styles.statusApproved]:
                          site.prerendering_status === "approved",
                        [styles.statusRequested]:
                          site.prerendering_status === "requested",
                        [styles.statusDisabled]:
                          site.prerendering_status === "disabled",
                      })}
                    >
                      {site.prerendering_status}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <NoData
                title="There is no sites here yet"
                description="Looks like it`s kinda empty here. Go ahead and add your first website!"
                path={routes.site.wizard({ tab: "new" })}
                buttonLabel="Add Site"
                blockWidth="100%"
                onClick={() => localStorage.removeItem("siteId")}
                disabled={!canCreate}
                iconHeight="100px"
              />
            )}
          </div>
        )}
      </div>

      <SiteModal
        onClose={() => setSiteId(null)}
        domain={domain}
        siteId={siteId}
      />
    </>
  );
};
